'use client';
import { getEventContentTexts } from '@eliadmin/core/src/utils/project-content-text';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';

import type { Event } from '@eliadmin/core/src/api/events/events-api';

import type { TranslateFn } from '@eliadmin/core/src/hooks/use-translations';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { HiOutlineLocationMarker } from 'react-icons/hi';
import { LuCalendarDays } from 'react-icons/lu';
import { Button } from '../ui/button';

const formatDateRange = (start: string, stop: string): string => {
  const startDate = new Date(start);
  const stopDate = new Date(stop);

  const startDay = String(startDate.getDate()).padStart(2, '0');
  const startMonth = String(startDate.getMonth() + 1).padStart(2, '0');
  const startFormatted = `${startDay}.${startMonth}.`;

  const stopDay = String(stopDate.getDate()).padStart(2, '0');
  const stopMonth = String(stopDate.getMonth() + 1).padStart(2, '0');
  const stopYear = stopDate.getFullYear();
  const stopFormatted = `${stopDay}.${stopMonth}.${stopYear}`;

  return `${startFormatted} - ${stopFormatted}`;
};

export const getDayTranslationString = (
  day: 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday',
  t: TranslateFn,
) => {
  switch (day) {
    case 'monday':
      return t('text1302');
    case 'tuesday':
      return t('text1303');
    case 'wednesday':
      return t('text1304');
    case 'thursday':
      return t('text1305');
    case 'friday':
      return t('text1306');
    case 'saturday':
      return t('text1307');
    case 'sunday':
      return t('text1308');
  }
};

export const translateCustomDate = (date: string, t: TranslateFn) => {
  return date
    .replace(/\bmonday\b/gi, getDayTranslationString('monday', t))
    .replace(/\btuesday\b/gi, getDayTranslationString('tuesday', t))
    .replace(/\bwednesday\b/gi, getDayTranslationString('wednesday', t))
    .replace(/\bthursday\b/gi, getDayTranslationString('thursday', t))
    .replace(/\bfriday\b/gi, getDayTranslationString('friday', t))
    .replace(/\bsaturday\b/gi, getDayTranslationString('saturday', t))
    .replace(/\bsunday\b/gi, getDayTranslationString('sunday', t));
};

export function EventCard({ hideButton = false, event }: { hideButton?: boolean; event: Event }) {
  const { t } = useTranslations();

  const customDate = getEventContentTexts(event)?.find(textField => textField.id === 4)?.value;

  const externalURL = getEventContentTexts(event)?.find(field => field.id === 3)?.value;

  return (
    <div
      className={cn(
        'col-span-1 flex min-h-[280px] w-full flex-col-reverse gap-4 bg-black sm:gap-0 lg:flex-row lg:justify-between',
        hideButton ? 'lg:items-start' : 'lg:items-center',
      )}
    >
      <div className="flex w-full flex-col items-start justify-start gap-4 p-4 sm:gap-6 sm:p-6 lg:gap-8 lg:p-8">
        <div className="flex flex-wrap items-start justify-start gap-4">
          {customDate ? (
            <div className="flex items-center justify-start gap-2">
              <LuCalendarDays size={20} className="text-primary" />
              <p className="whitespace-nowrap text-xs font-normal text-white sm:text-sm">
                {translateCustomDate(customDate, t)}
              </p>
            </div>
          ) : event?.date_start && event?.date_stop ? (
            <div className="flex items-center justify-start gap-2">
              <LuCalendarDays size={20} className="text-primary" />
              <p className="whitespace-nowrap text-xs font-normal text-white sm:text-sm">
                {formatDateRange(event.date_start, event.date_stop)}
              </p>
            </div>
          ) : (
            <div className="flex items-center justify-start gap-2">
              <LuCalendarDays size={20} className="text-primary" />
              <p className="text-sm text-white">{t('text2490')}</p>
            </div>
          )}
          {event?.location_name && (
            <div className="flex items-center justify-start gap-2">
              <HiOutlineLocationMarker size={20} className="text-primary" />
              <p className="text-xs font-normal text-white sm:text-sm">{event?.location_name}</p>
            </div>
          )}
        </div>
        <h3
          data-ea-visual-tag="h4"
          className="text-balance font-heading text-2xl font-medium tracking-tight text-white sm:h-16 lg:h-[72px] lg:text-3xl"
        >
          {event.name}
        </h3>
        <div className="flex w-full flex-col items-start justify-start gap-2 sm:flex-row sm:flex-wrap">
          {!hideButton && (
            <Button asChild variant={'secondary'} className="w-full disabled:opacity-50 sm:w-fit">
              <Link href={event?.slug} aria-label={`${t('text792')}: ${event.name}`}>
                {t('text792')}
              </Link>
            </Button>
          )}
          {!!externalURL && (
            <Button asChild className="w-full disabled:opacity-50 sm:w-fit">
              <Link
                prefetch={false}
                href={externalURL}
                target="_blank"
                aria-label={`${t('text3751')}: ${event.name}`}
              >
                {t('text3751')}
              </Link>
            </Button>
          )}
        </div>
      </div>
      <Image
        src={event?.image?.url ?? '/images/event-template.jpg'}
        alt={resolveImageAlt(event?.image, event?.name)}
        width={0}
        height={0}
        className="h-[150px] w-full object-cover object-center sm:h-[220px] lg:h-full lg:max-w-[200px]"
        sizes="(max-width: 640px) 100vw, 300px"
      />
    </div>
  );
}
